import { type OrgLike, getActivePlugins } from "./orgPlugins";

/** Opaque contribution registered on a plugin; typed at the descriptor site. */
export type PluginHookContribution = {
  readonly hookId: string;
  run: (ctx: unknown) => unknown;
};

export type PluginHookMergeMeta = {
  /** Active plugin that produced this contribution. */
  pluginKey: string;
};

export type PluginHook<Context, Result> = {
  readonly id: string;
  initial(): Result;
  merge(
    accumulated: Result,
    contribution: Result,
    meta: PluginHookMergeMeta
  ): Result;
  contribute(fn: (ctx: Context) => Result | void): PluginHookContribution;
  run(organization: OrgLike, ctx: Context): Result;
};

type DefinePluginHookConfig<Result> = {
  name: string;
  initial: () => Result;
  /**
   * Combine contributions from active plugins. `meta.pluginKey` identifies the
   * plugin that produced `contribution` (for conflict errors / debug).
   */
  merge: (
    accumulated: Result,
    contribution: Result,
    meta: PluginHookMergeMeta
  ) => Result;
};

export function definePluginHook<Context, Result>(
  config: DefinePluginHookConfig<Result>
): PluginHook<Context, Result> {
  const contribute = (
    fn: (ctx: Context) => Result | void
  ): PluginHookContribution => ({
    hookId: config.name,
    run: fn as (ctx: unknown) => unknown,
  });

  const run = (organization: OrgLike, ctx: Context): Result => {
    const plugins = getActivePlugins(organization);
    const collected: { pluginKey: string; value: Result }[] = [];

    for (const plugin of plugins) {
      for (const contribution of plugin.hooks ?? []) {
        if (contribution.hookId !== config.name) {
          continue;
        }

        const next = contribution.run(ctx) as Result | void;
        if (next === undefined) {
          continue;
        }

        collected.push({ pluginKey: plugin.key, value: next });
      }
    }

    if (collected.length === 0) {
      return config.initial();
    }

    return collected
      .slice(1)
      .reduce(
        (acc, c) => config.merge(acc, c.value, { pluginKey: c.pluginKey }),
        collected[0].value
      );
  };

  return {
    id: config.name,
    initial: config.initial,
    merge: config.merge,
    contribute,
    run,
  };
}

/** Runs a host-owned hook descriptor for the org and merges plugin contributions. */
export function runPluginHook<Context, Result>(
  hook: PluginHook<Context, Result>,
  organization: OrgLike,
  ctx: Context
): Result {
  return hook.run(organization, ctx);
}
